import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AppSettings, DeveloperProfile } from '@ace-tracker/shared';
import { fetchSettings, UnauthorizedError } from './api';

export const SETTINGS_QUERY_KEY = ['settings'] as const;

/** Admin settings (Settings view). Shared by the whole app — display names are needed on every view. */
export function useSettings() {
  return useQuery<AppSettings>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: fetchSettings,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
  });
}

/**
 * Looks up a developer's admin profile. Falls back to the raw id when no
 * display name is configured (or settings haven't loaded), so the dashboard
 * never blocks on settings.
 */
export function useDeveloperProfiles() {
  const { data } = useSettings();
  const byId = useMemo(
    () => new Map<string, DeveloperProfile>((data?.developers ?? []).map((d) => [d.developerId.toLowerCase(), d])),
    [data],
  );
  const profile = useCallback((developerId: string) => byId.get(developerId.toLowerCase()), [byId]);
  const label = useCallback((developerId: string) => profile(developerId)?.displayName ?? developerId, [profile]);
  return { profile, label };
}

/**
 * A developer cell: the display name when one is set, with the raw id beneath
 * it (so the id stays searchable/copyable); otherwise just the id, in mono.
 */
export function DeveloperName({ developerId, showTeam = false }: { developerId: string; showTeam?: boolean }) {
  const { profile } = useDeveloperProfiles();
  const p = profile(developerId);
  if (!p?.displayName) {
    return <span className="font-mono text-xs text-text-default">{developerId}</span>;
  }
  return (
    <span className="inline-flex flex-col" title={developerId}>
      <span className="text-sm font-medium text-text-default">{p.displayName}</span>
      {showTeam && p.team && <span className="text-[11px] text-text-faint">{p.team}</span>}
    </span>
  );
}
